import clsx from 'clsx'
import { useMemo, useState } from 'react'

import { Category } from '../../models/Category'
import { Dashboard } from '../../models/Dashboard'
import { Item } from '../../models/Item'
import { currencyFormat } from '../../utils/numbers'
import { Icon, IconNameAndCategoryString } from '../Icon'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import SegmentedControl from '../app/design/SegmentedControl'
import { Clickable } from '../common/Clickable'
import { EmptyDashboardLayout } from '../dashboards/EmptyDashboardLayout'
import { Chip } from '../common/fields/Chip'
import { SearchField } from '../common/fields/SearchField'

type Props = {
    items: Item[]
    categories: Category[]
    onSelect: (item: Item) => void
}

function PlaceholderIcon({ className }: { className?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            className={className}
        >
            <path
                d="M25.3333 4H6.66667C5.19391 4 4 5.19391 4 6.66667V25.3333C4 26.8061 5.19391 28 6.66667 28H25.3333C26.8061 28 28 26.8061 28 25.3333V6.66667C28 5.19391 26.8061 4 25.3333 4Z"
                stroke="#545454"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M11.3359 13.332C12.4405 13.332 13.3359 12.4366 13.3359 11.332C13.3359 10.2275 12.4405 9.33203 11.3359 9.33203C10.2314 9.33203 9.33594 10.2275 9.33594 11.332C9.33594 12.4366 10.2314 13.332 11.3359 13.332Z"
                stroke="#545454"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M27.9974 19.9987L21.3307 13.332L6.66406 27.9987"
                stroke="#545454"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

type Filter = 'all' | 'service' | 'product' | 'class' | 'reservation'

const FILTER_OPTIONS: { label: string; value: Filter; icon: IconNameAndCategoryString }[] = [
    { label: 'All items', value: 'all', icon: 'box.small' },
    { label: 'Services', value: 'service', icon: 'tag.small' },
    { label: 'Products', value: 'product', icon: 'package.small' },
    { label: 'Classes', value: 'class', icon: 'student.small' },
    { label: 'Reservations', value: 'reservation', icon: 'calendarClock.small' },
]

function ItemGridDisplay({ item, onClick }: { item: Item; onClick: () => void }) {
    return (
        <div
            className={clsx(
                'group relative h-[161px] w-[218px] cursor-pointer rounded-lg bg-gray-200 outline outline-gray-300',
                'hover:outline-secondary-400 hover:outline-2'
            )}
            onClick={onClick}
        >
            {item.image?.uri ? (
                <img
                    src={item.image?.uri}
                    alt={item.name}
                    className="h-full w-full rounded-lg object-cover pb-1"
                />
            ) : (
                <PlaceholderIcon className="mx-auto mt-9" />
            )}
            <div className="absolute bottom-0 h-14 w-full rounded-b-lg bg-white p-3 text-center active:bg-white active:bg-linear-to-b active:from-black/8 active:to-black/8">
                <Label size="sm" className="line-clamp-2">
                    {item.name}
                </Label>
            </div>
            <div className="absolute top-3 left-3 flex h-6 flex-col items-center rounded-full border border-white bg-white px-2 py-1">
                <Label size="sm">{currencyFormat(item.amount, true)}</Label>
            </div>
        </div>
    )
}

function ItemListDisplay({ item, onClick }: { item: Item; onClick: () => void }) {
    return (
        <Clickable
            className="flex cursor-pointer items-center gap-4 rounded-lg p-[15px] outline outline-gray-300"
            onClick={onClick}
        >
            {item.image?.uri ? (
                <img
                    src={item.image?.uri}
                    alt={item.name}
                    className="size-12 rounded-lg object-cover"
                />
            ) : (
                <div className="flex size-12 items-center justify-center rounded-lg bg-gray-200">
                    <PlaceholderIcon className="size-6" />
                </div>
            )}
            <div className="flex flex-col gap-1 text-left">
                <Label size="md" className="line-clamp-2 text-ellipsis">
                    {item.name}
                </Label>
                <Paragraph size="sm" className="text-gray-600">
                    {currencyFormat(item.amount, true)}
                </Paragraph>
            </div>
        </Clickable>
    )
}

export function NewChargeItemsView({ items, categories, onSelect }: Props) {
    const [view, setView] = useState<'grid' | 'list'>('grid')

    const [filter, setFilter] = useState<Filter>('all')
    const [searchText, setSearchText] = useState('')

    const filteredItems = useMemo(() => {
        const queryTokens = searchText.toLowerCase().split(/\s+/) ?? []

        if (filter === 'all') {
            return items.filter((i) => {
                return queryTokens.every((token) => i.name?.toLowerCase()?.includes(token))
            })
        }

        return items.filter(
            (i) =>
                i.type === filter &&
                queryTokens.every((token) => i.name?.toLowerCase().includes(token))
        )
    }, [items, filter, searchText])

    const FILTER_OPTIONS_WITH_COUNT = useMemo(
        () =>
            FILTER_OPTIONS.filter((f) => {
                if (f.value === 'all') return true

                return items.filter((i) => i.type === f.value).length > 0
            }),
        [items]
    )

    return (
        <div className="mt-6">
            <div className="mb-4 flex items-center justify-between gap-4">
                <SearchField
                    placeholder="Search items"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    isFullWidth
                    onClear={() => setSearchText('')}
                />
                <SegmentedControl
                    size="small"
                    segments={[
                        {
                            label: 'Grid',
                            value: 'grid',
                            icon: <Icon icon="grid.small" shouldInheritColor />,
                        },
                        {
                            label: 'List',
                            value: 'list',
                            icon: <Icon icon="list2.small" shouldInheritColor />,
                        },
                    ]}
                    value={view}
                    className="w-[180px]"
                    compact
                    onChange={(value) => setView(value as 'grid' | 'list')}
                />
            </div>

            <div className="flex items-center gap-2">
                {FILTER_OPTIONS_WITH_COUNT.map((f) => (
                    <Chip
                        icon={f.icon}
                        key={f.value}
                        size="small"
                        title={f.label}
                        isSelected={filter === f.value}
                        onClick={() => setFilter(f.value)}
                    />
                ))}
            </div>

            {view === 'grid' ? (
                <div className="mt-6 flex flex-wrap gap-3">
                    {filteredItems.map((item) => (
                        <ItemGridDisplay key={item.id} item={item} onClick={() => onSelect(item)} />
                    ))}
                </div>
            ) : (
                <div className="mt-6 flex flex-col gap-3">
                    {filteredItems.map((item) => (
                        <ItemListDisplay key={item.id} item={item} onClick={() => onSelect(item)} />
                    ))}
                </div>
            )}

            {Boolean(searchText) && filteredItems.length === 0 && (
                <div className="-m-1 -mt-2">
                    <EmptyDashboardLayout
                        dashboard={new Dashboard({ title: 'Items' })}
                        searchtext={searchText}
                    />
                </div>
            )}
        </div>
    )
}
